'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Play } from 'lucide-react';
import { parseVideoUrl } from '@/lib/funnel/video';
import type { VideoOrientation } from '@/lib/funnel/types';

// Facade-pattern video embed: renders only a poster (or a branded placeholder)
// with a play button until clicked, so funnel pages load zero third-party JS
// up front. Direct video files render a native <video> immediately.
export default function VideoEmbed({
  url,
  orientation,
  poster,
  title,
}: {
  url: string;
  orientation: VideoOrientation;
  poster?: string;
  title?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const parsed = parseVideoUrl(url);

  if (parsed.kind === 'unknown') return null;

  const aspectClasses =
    orientation === 'portrait'
      ? 'aspect-[9/16] w-full max-w-sm'
      : 'aspect-video w-full max-w-3xl';

  if (parsed.kind === 'file') {
    return (
      <video
        controls
        preload="metadata"
        poster={poster || undefined}
        src={parsed.src}
        className={`${aspectClasses} mx-auto rounded-2xl bg-deep-navy object-contain shadow-xl`}
      />
    );
  }

  if (playing) {
    const joiner = parsed.embedUrl.includes('?') ? '&' : '?';
    return (
      <div className={`${aspectClasses} mx-auto overflow-hidden rounded-2xl shadow-xl`}>
        <iframe
          src={`${parsed.embedUrl}${joiner}autoplay=1`}
          title={title || 'Video'}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={title ? `Play video: ${title}` : 'Play video'}
      className={`${aspectClasses} group relative mx-auto block overflow-hidden rounded-2xl bg-deep-navy shadow-xl`}
    >
      {poster ? (
        <Image
          src={poster}
          alt={title || 'Video preview'}
          fill
          sizes="(max-width: 768px) 100vw, 768px"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-deep-navy via-[#27435F] to-soft-teal-dark" />
      )}
      <span className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
      <span className="absolute left-1/2 top-1/2 grid h-20 w-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/95 shadow-2xl transition-transform group-hover:scale-110">
        <Play className="ml-1 h-9 w-9 fill-soft-teal-dark text-soft-teal-dark" />
      </span>
    </button>
  );
}
